/**
 * AdminMediaLibraryPage — everything anybody has uploaded, in one place.
 *
 * Three jobs, in the order people need them: find a file, reuse it, or replace
 * it. Replacing writes over the same address, so a logo corrected here is
 * corrected on every page showing it — which is the whole reason this exists.
 *
 * Route: /admin/media-library
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Search, Loader2, Copy, RefreshCw, EyeOff, Eye, Images, HardDrive,
  ChevronLeft, ChevronRight, Check, Pencil, ArrowLeft, CopyCheck,
} from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import {
  searchLibrary, libraryFacets, renameAsset, hideAsset, replaceAsset, prettySize,
} from '@/lib/mediaLibrary';
import { AssetThumb, absolute } from '@/components/media/AssetThumb';
import { DuplicateFinder } from '@/components/media/DuplicateFinder';

const KINDS = ['image', 'video', 'audio', 'document'];

const Stat = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2.5">
    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-900 text-white">
      <Icon className="h-4 w-4" />
    </span>
    <div>
      <p className="text-sm font-semibold text-slate-900">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  </div>
);

const AdminMediaLibraryPage = () => {
  const queryClient = useQueryClient();
  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  const [kind, setKind] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [renaming, setRenaming] = useState('');
  const [progress, setProgress] = useState(0);
  const [dupes, setDupes] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    const id = setTimeout(() => { setTerm(q.trim()); setPage(1); }, 300);
    return () => clearTimeout(id);
  }, [q]);

  const { data, isLoading } = useQuery({
    queryKey: ['media-library', term, kind, page],
    queryFn: () => searchLibrary({ q: term, kind, page, per_page: 48 }),
  });
  const { data: facets } = useQuery({
    queryKey: ['media-library-facets'],
    queryFn: libraryFacets,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['media-library'] });
    queryClient.invalidateQueries({ queryKey: ['media-library-facets'] });
  };

  const replacing = useMutation({
    mutationFn: ({ id, file }) => replaceAsset(id, file, setProgress),
    onSuccess: (asset) => {
      setSelected(asset);
      setProgress(0);
      toast.success('Replaced everywhere it is used');
      refresh();
    },
    onError: (e) => { setProgress(0); toast.error(e.message); },
  });

  const hiding = useMutation({
    mutationFn: ({ id, hidden }) => hideAsset(id, hidden),
    onSuccess: (asset) => {
      setSelected(asset);
      toast.success(asset.hidden ? 'Hidden from the library' : 'Back in the library');
      refresh();
    },
    onError: () => toast.error('That did not work'),
  });

  const naming = useMutation({
    mutationFn: ({ id, name }) => renameAsset(id, name),
    onSuccess: (asset) => { setSelected(asset); setRenaming(''); refresh(); },
    onError: () => toast.error('That name could not be saved'),
  });

  const copy = (asset) => {
    navigator.clipboard?.writeText(absolute(asset.url));
    toast.success('Link copied');
  };

  const assets = data?.assets || [];
  const pages = data?.pages || 1;

  return (
    <div className="space-y-5 p-4 sm:p-6" data-testid="media-library-page">
      <Link to="/admin"
            className="inline-flex items-center gap-1 text-sm text-slate-500 transition-colors hover:text-slate-900"
            data-testid="media-library-back">
        <ArrowLeft className="h-3.5 w-3.5" />Admin
      </Link>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">Media Library</h1>
          <p className="mt-1 text-sm text-slate-500">
            Every photo, video, recording and document already uploaded — search
            it, reuse it, or put a better version behind the same link.
          </p>
        </div>
        <div className="flex items-center gap-6">
          <Stat icon={Images} label="files" value={facets?.total ?? '—'} />
          <Stat icon={HardDrive} label="stored" value={facets ? prettySize(facets.bytes) : '—'} />
          <Button variant="outline" onClick={() => setDupes(true)}
                  data-testid="media-library-duplicates">
            <CopyCheck className="mr-2 h-4 w-4" />Find duplicates
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search by name, or by what it was uploaded for"
                 className="pl-8" data-testid="media-library-search" />
        </div>
        <Button type="button" size="sm" variant={kind === '' ? 'default' : 'outline'}
                onClick={() => { setKind(''); setPage(1); }}
                data-testid="media-library-kind-all">
          Everything
        </Button>
        {KINDS.map((k) => (
          <Button key={k} type="button" size="sm"
                  variant={kind === k ? 'default' : 'outline'}
                  onClick={() => { setKind(k); setPage(1); }}
                  data-testid={`media-library-kind-${k}`}>
            {k}s
            {facets?.kinds?.[k] ? (
              <span className="ml-1.5 text-[10px] opacity-60">{facets.kinds[k]}</span>
            ) : null}
          </Button>
        ))}
      </div>

      {isLoading ? (
        <p className="py-16 text-center text-sm text-slate-500">
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Loading the library…
        </p>
      ) : assets.length === 0 ? (
        <Card><CardContent className="py-16 text-center" data-testid="media-library-empty">
          <Images className="mx-auto mb-3 h-10 w-10 text-slate-300" />
          <p className="font-semibold text-slate-700">Nothing here yet</p>
          <p className="mt-1 text-sm text-slate-500">
            Files appear the moment anybody uploads one, anywhere in the app.
          </p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
          {assets.map((a) => (
            <button key={a.id} type="button"
                    onClick={() => { setSelected(a); setRenaming(''); }}
                    className="group overflow-hidden rounded-xl border border-slate-200
                               bg-white text-left transition-all hover:-translate-y-0.5
                               hover:border-slate-900 hover:shadow-lg"
                    data-testid={`media-library-asset-${a.id}`}>
              <div className="aspect-square bg-slate-50">
                <AssetThumb asset={a} />
              </div>
              <div className="p-2.5">
                <p className="truncate text-xs font-medium text-slate-800">{a.name}</p>
                <p className="mt-0.5 text-[10px] uppercase tracking-wide text-slate-400">
                  {a.purpose} · {prettySize(a.size)}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-center gap-3 pt-2">
          <Button variant="outline" size="sm" disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)} data-testid="media-library-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-xs text-slate-500">Page {page} of {pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages}
                  onClick={() => setPage((p) => p + 1)} data-testid="media-library-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      <Sheet open={!!selected} onOpenChange={(v) => { if (!v) setSelected(null); }}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-md"
                      data-testid="media-library-detail">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="pr-6 text-left text-base">{selected.name}</SheetTitle>
              </SheetHeader>

              <div className="mt-4 overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                {selected.kind === 'image' ? (
                  <img src={absolute(selected.preview_url)} alt={selected.name}
                       className="max-h-64 w-full object-contain"
                       data-testid="media-library-detail-preview" />
                ) : selected.kind === 'video' ? (
                  <video src={absolute(selected.preview_url)} controls preload="metadata"
                         className="max-h-64 w-full bg-black" />
                ) : selected.kind === 'audio' ? (
                  <audio src={absolute(selected.preview_url)} controls className="w-full p-3" />
                ) : (
                  <div className="h-28"><AssetThumb asset={selected} /></div>
                )}
              </div>

              <dl className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-slate-500">Uploaded for</dt>
                  <dd className="text-right"><Badge variant="outline">{selected.purpose}</Badge></dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-slate-500">Size</dt>
                  <dd className="text-slate-800">
                    {prettySize(selected.size)}
                    {selected.width ? ` · ${selected.width}×${selected.height}` : ''}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-slate-500">Added</dt>
                  <dd className="text-slate-800">
                    {selected.uploaded_at ? selected.uploaded_at.slice(0, 10) : '—'}
                  </dd>
                </div>
                {selected.replaced && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-slate-500">Replaced</dt>
                    <dd className="text-slate-800">{selected.updated_at.slice(0, 10)}</dd>
                  </div>
                )}
              </dl>

              <div className="mt-4 space-y-2">
                {renaming ? (
                  <div className="flex gap-2">
                    <Input value={renaming} onChange={(e) => setRenaming(e.target.value)}
                           data-testid="media-library-rename-input" />
                    <Button size="sm"
                            onClick={() => naming.mutate({ id: selected.id, name: renaming })}
                            data-testid="media-library-rename-save">
                      <Check className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <Button variant="outline" className="w-full justify-start"
                          onClick={() => setRenaming(selected.name)}
                          data-testid="media-library-rename">
                    <Pencil className="mr-2 h-4 w-4" />Rename
                  </Button>
                )}

                <Button variant="outline" className="w-full justify-start"
                        onClick={() => copy(selected)} data-testid="media-library-copy">
                  <Copy className="mr-2 h-4 w-4" />Copy link
                </Button>

                <input ref={fileRef} type="file" hidden
                       onChange={(e) => {
                         const file = e.target.files?.[0];
                         if (file) replacing.mutate({ id: selected.id, file });
                         e.target.value = '';
                       }}
                       data-testid="media-library-replace-input" />
                <Button className="w-full justify-start"
                        disabled={replacing.isPending}
                        onClick={() => fileRef.current?.click()}
                        data-testid="media-library-replace">
                  {replacing.isPending
                    ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    : <RefreshCw className="mr-2 h-4 w-4" />}
                  {replacing.isPending ? `Replacing… ${progress}%` : 'Replace this file'}
                </Button>
                <p className="px-1 text-[11px] leading-relaxed text-slate-500">
                  The new file keeps the same link, so every page using it updates.
                  A browser that has already seen the old one may hold onto it for
                  a while.
                </p>

                <Button variant="ghost" className="w-full justify-start text-slate-500"
                        onClick={() => hiding.mutate({ id: selected.id, hidden: !selected.hidden })}
                        data-testid="media-library-hide">
                  {selected.hidden
                    ? <><Eye className="mr-2 h-4 w-4" />Show in the library</>
                    : <><EyeOff className="mr-2 h-4 w-4" />Hide from the library</>}
                </Button>
                <p className="px-1 text-[11px] leading-relaxed text-slate-500">
                  Hiding only tidies this list. The file keeps working wherever it
                  is already used — nothing is deleted.
                </p>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <DuplicateFinder open={dupes} onOpenChange={setDupes} />
    </div>
  );
};

export default AdminMediaLibraryPage;
